import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown, Users, TrendingUp, GraduationCap, Handshake } from "lucide-react";
import Page from "@/components/Page";
import Reveal from "@/components/Reveal";
import MaskedLine from "@/components/MaskedLine";
import EditorialMarquee from "@/components/Marquee";
import ContactBanner from "@/components/ContactBanner";
import { useContent, imgSrc } from "@/content/ContentContext";

const ICONS = [Users, TrendingUp, GraduationCap, Handshake];
const SPANS = ["md:col-span-7", "md:col-span-5", "md:col-span-5", "md:col-span-7"];

const Home = () => {
  const { content } = useContent();
  const { site, home } = content;
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <Page data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen bg-pine text-cream grain overflow-hidden flex items-center" data-testid="home-hero">
        <motion.div style={{ y: imgY }} className="absolute inset-y-[-10%] right-0 w-full lg:w-[58%]" aria-hidden="true">
          <img
            src={imgSrc(home.heroImage)}
            alt=""
            className="h-full w-full object-cover opacity-[0.22] mix-blend-luminosity"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-pine via-pine/60 to-pine/20" />
        </motion.div>

        <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-5 sm:px-8 pt-32 pb-24">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex items-center gap-3 text-xs font-semibold tracking-[0.32em] uppercase text-gold mb-8"
          >
            <span className="h-px w-10 bg-gold inline-block" />
            {home.heroOverline}
          </motion.p>

          <h1 className="font-serif font-medium leading-[1.02] tracking-tight text-5xl sm:text-7xl lg:text-[5.6rem]">
            <MaskedLine delay={0.35}>{home.heroLine1}</MaskedLine>
            <MaskedLine delay={0.47}>
              <span className="italic text-gold">{home.heroEmphasis}</span> {home.heroLine2Suffix}
            </MaskedLine>
            <MaskedLine delay={0.59}>{home.heroLine3}</MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.95 }}
            className="mt-8 max-w-xl text-base sm:text-lg text-cream/70 leading-relaxed"
          >
            {home.heroIntro}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.1 }}
            className="mt-12 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/contact"
              data-testid="hero-cta-get-in-touch"
              className="group inline-flex items-center gap-2 rounded-full bg-gold px-7 py-3.5 text-sm font-semibold text-pine transition-colors duration-300 hover:bg-cream"
            >
              {site.ctaPrimary}
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <Link
              to="/contact"
              data-testid="hero-cta-discuss-needs"
              className="inline-flex items-center gap-2 rounded-full border border-cream/30 px-7 py-3.5 text-sm font-semibold text-cream transition-colors duration-300 hover:border-gold hover:text-gold"
            >
              {site.ctaSecondary}
            </Link>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 0.8 }}
          className="absolute bottom-8 left-5 sm:left-8 z-10 flex items-center gap-3 text-cream/50 text-xs tracking-[0.25em] uppercase"
        >
          <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}>
            <ArrowDown size={14} />
          </motion.span>
          Scroll
        </motion.div>
      </section>

      <EditorialMarquee />

      {/* MANIFESTO STRIP */}
      <section className="bg-cream" data-testid="home-intro">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-24 sm:py-32 grid gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-4">
            <p className="text-xs font-semibold tracking-[0.32em] uppercase text-gold-muted">{home.manifestoOverline}</p>
            <p className="mt-4 font-serif text-6xl text-pine/20 font-medium">01</p>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-8">
            <p className="font-serif text-3xl sm:text-4xl lg:text-[2.75rem] leading-[1.2] text-pine max-w-3xl">
              {home.manifestoText}
            </p>
            <Link
              to="/about"
              data-testid="home-intro-about-link"
              className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-pine link-underline-gold pb-1"
            >
              {home.manifestoLink} <ArrowUpRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section className="bg-cream pb-24 sm:pb-32" data-testid="home-services">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
            <Reveal>
              <p className="text-xs font-semibold tracking-[0.32em] uppercase text-gold-muted mb-4">{home.servicesOverline}</p>
              <h2 className="font-serif text-4xl sm:text-5xl font-medium text-pine leading-tight">
                {home.servicesTitle}
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <Link
                to="/services"
                data-testid="home-services-view-all-link"
                className="group inline-flex items-center gap-2 rounded-full border border-pine/25 px-6 py-3 text-sm font-semibold text-pine transition-colors duration-300 hover:bg-pine hover:text-cream"
              >
                {home.servicesCta}
                <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </Reveal>
          </div>

          <div className="grid gap-5 md:grid-cols-12">
            {home.services.map((s, i) => {
              const Icon = ICONS[i % ICONS.length];
              return (
                <Reveal key={i} delay={i * 0.08} className={SPANS[i % SPANS.length]}>
                  <Link
                    to="/services"
                    data-testid={`home-service-card-${(s.title || `service-${i}`).toLowerCase().replace(/\s+/g, "-")}`}
                    className="group relative block overflow-hidden border border-line bg-white h-full"
                  >
                    <div className="relative h-56 sm:h-64 overflow-hidden">
                      <img
                        src={imgSrc(s.image)}
                        alt={s.title}
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.05]"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-pine/85 via-pine/20 to-transparent" />
                      <span className="absolute top-5 left-5 font-serif text-lg text-cream/80">{String(i + 1).padStart(2, "0")}</span>
                      <span className="absolute top-5 right-5 inline-flex h-10 w-10 items-center justify-center rounded-full bg-pine/80 backdrop-blur text-gold">
                        <Icon size={17} />
                      </span>
                    </div>
                    <div className="p-7 sm:p-8">
                      <h3 className="font-serif text-2xl sm:text-3xl font-medium text-pine">{s.title}</h3>
                      <p className="mt-3 text-sm leading-relaxed text-ink/65">{s.text}</p>
                      <span className="mt-5 inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase text-gold-muted">
                        Learn more
                        <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      </span>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ABOUT PREVIEW */}
      <section className="bg-white border-y border-line" data-testid="home-about-preview">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-24 sm:py-32 grid gap-14 lg:grid-cols-2 items-center">
          <Reveal className="relative">
            <div className="overflow-hidden border border-line">
              <motion.img
                src={imgSrc(home.aboutImage)}
                alt="About the practice"
                loading="lazy"
                initial={{ scale: 1.12 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
                className="h-[420px] sm:h-[520px] w-full object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -right-4 sm:-right-6 bg-pine text-cream px-7 py-6 grain">
              <p className="relative z-10 font-serif text-xl italic text-gold">{home.aboutBadge}</p>
            </div>
          </Reveal>
          <div>
            <Reveal>
              <p className="text-xs font-semibold tracking-[0.32em] uppercase text-gold-muted mb-5">{home.aboutOverline}</p>
              <h2 className="font-serif text-4xl sm:text-5xl font-medium text-pine leading-tight">
                {home.aboutTitle1}<br />{home.aboutTitle2}
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-7 text-base leading-relaxed text-ink/70">{home.aboutP1}</p>
              <p className="mt-4 text-base leading-relaxed text-ink/70">{home.aboutP2}</p>
            </Reveal>
            <Reveal delay={0.18}>
              <Link
                to="/about"
                data-testid="home-about-link"
                className="mt-9 inline-flex items-center gap-2 rounded-full bg-pine px-7 py-3.5 text-sm font-semibold text-cream transition-colors duration-300 hover:bg-gold hover:text-pine"
              >
                {home.aboutCta} <ArrowUpRight size={15} />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      <ContactBanner overline={home.banner.overline} title={home.banner.title} blurb={home.banner.blurb} />
    </Page>
  );
};

export default Home;
